import { useEffect, useState, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuGroup } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { User, Settings, LogOut, Shield, FileText, Search, LayoutDashboard, ArrowRightLeft, Users, Activity, Image } from 'lucide-react';

// Lazy-load AuthModal (and its validation deps like zod) only when needed
const LazyAuthModal = lazy(() =>
  import('./AuthModal').then((m) => ({ default: m.AuthModal }))
);

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  blog_editor: 'Editor',
  operatore_business: 'Business',
  membro_forze_armate: 'Forze Armate',
  utente: 'Utente',
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  blog_editor: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  operatore_business: 'bg-green-500/10 text-green-600 border-green-500/20',
  membro_forze_armate: 'bg-red-500/10 text-red-600 border-red-500/20',
  utente: 'bg-muted text-muted-foreground border-border',
};

export const UserMenu = () => {
  const { user, signOut, loading } = useAuth();
  const { userRole, isAdminOrEditor, isAdmin, isSuperAdmin } = useUserRole();
  const location = useLocation();
  const [authModalOpen, setAuthModalOpen] = useState(false);

  // Chiudi automaticamente il modal se l'utente risulta autenticato
  useEffect(() => {
    if (user && authModalOpen) setAuthModalOpen(false);
  }, [user, authModalOpen]);

  if (loading || !user) {
    return (
      <>
        <Button 
          variant="ghost" 
          onClick={() => setAuthModalOpen(true)} 
          disabled={loading}
          className="h-10 px-2 gap-2 text-foreground hover:bg-muted"
          aria-label="Accedi o Registrati"
        >
          <User className="h-5 w-5" />
          <span className="hidden xl:inline text-sm">Accedi</span>
        </Button>
        {authModalOpen && (
          <Suspense fallback={null}>
            <LazyAuthModal
              open={authModalOpen}
              onOpenChange={setAuthModalOpen}
              returnUrl={location.pathname}
            />
          </Suspense>
        )}
      </>
    );
  }

  const userInitials = user.user_metadata?.full_name?.split(' ').map((name: string) => name[0]).join('').toUpperCase() || user.email?.[0]?.toUpperCase() || 'U';
  const roleLabel = userRole ? ROLE_LABELS[userRole] : 'Utente';
  const roleColor = userRole ? ROLE_COLORS[userRole] : ROLE_COLORS.utente;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.user_metadata?.avatar_url} alt="Immagine profilo utente" />
            <AvatarFallback>{userInitials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium leading-none">
                {user.user_metadata?.full_name || 'Utente'}
              </p>
              <Badge variant="outline" className={`text-xs ${roleColor}`}>
                {roleLabel}
              </Badge>
            </div>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/profilo" className="cursor-pointer">
              <User className="mr-2 h-4 w-4" />
              <span>Profilo</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/impostazioni" className="cursor-pointer">
              <Settings className="mr-2 h-4 w-4" />
              <span>Impostazioni</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        {isAdminOrEditor() && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-2 py-1.5">
              Gestione
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to="/dashboard" className="cursor-pointer">
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  <span>Dashboard</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/dashboard/blog" className="cursor-pointer">
                  <FileText className="mr-2 h-4 w-4" />
                  <span>Blog</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/dashboard/entita/fabbriche" className="cursor-pointer">
                  <Shield className="mr-2 h-4 w-4" />
                  <span>Entità</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/dashboard/media" className="cursor-pointer">
                  <Image className="mr-2 h-4 w-4" />
                  <span>Media Library</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}

        {isAdmin() && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-2 py-1.5">
              Strumenti
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to="/dashboard/admin/seo" className="cursor-pointer">
                  <Search className="mr-2 h-4 w-4" />
                  <span>SEO</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/dashboard/landing-pages" className="cursor-pointer">
                  <Activity className="mr-2 h-4 w-4" />
                  <span>Landing Pages</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/dashboard/admin/roles" className="cursor-pointer">
                  <Users className="mr-2 h-4 w-4" />
                  <span>Ruoli Utenti</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut} className="cursor-pointer text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
